/* eslint-disable no-useless-escape */
import { constants } from 'utils/paymentData';
import { isEmpty } from 'utils/types';
import { checkVAT, countries } from 'jsvat';
import { captureError } from 'sentry';

const validateEmpty = (obj, key) => {
  if (isEmpty(obj) || isEmpty(key) || isEmpty(obj[key]))
    return { [key]: 'empty' };
  return {};
};

const smellsLikeIban = (str) =>
  /^([A-Z]{2}[ \-]?[0-9]{2})(?=(?:[ \-]?[A-Z0-9]){9,30}$)((?:[ \-]?[A-Z0-9]{3,5}){2,7})([ \-]?[A-Z0-9]{1,3})?$/.test(
    str
  );

const validateIbanChecksum = (str) => {
  const ibanStripped = str
    .replace(/[^A-Z0-9]+/gi, '') // keep numbers and letters only
    .toUpperCase(); // calculation expects upper-case
  const m = ibanStripped.match(/^([A-Z]{2})([0-9]{2})([A-Z0-9]{9,30})$/);
  if (!m) return false;

  const numbericed = (m[3] + m[1] + m[2]).replace(
    /[A-Z]/g,
    (ch) => ch.charCodeAt(0) - 55
  ); // replace upper-case characters by numbers 10 to 35

  // The resulting number would be to long for javascript to handle without loosing precision.
  // So the trick is to chop the string up in smaller parts.
  const mod97 = numbericed
    .match(/\d{1,7}/g)
    .reduce((total, curr) => Number(total + curr) % 97, '');

  return mod97 === 1;
};

const validateIBAN = (iban) =>
  smellsLikeIban(iban.trim()) && validateIbanChecksum(iban.trim());

const validateIBANIfNeeded = (paymentMethod) => {
  if (paymentMethod && paymentMethod.payment_method !== 'DIRECT_DEBIT')
    return {};
  if (
    validateIBAN(
      paymentMethod &&
        paymentMethod.payment_term &&
        paymentMethod.payment_term.IBAN
    )
  ) {
    return {};
  }

  return { IBAN: 'not valid' };
};

const validateTaxNumber = ({ taxNumber }) => {
  try {
    const isTaxNumberValid = checkVAT(taxNumber, countries);
    if (isTaxNumberValid.isValid === false)
      throw Error(`TAX number invalid ${JSON.stringify(isTaxNumberValid)}`);
  } catch (e) {
    captureError('Tax number', e);
  }
  return {};
  // return { tax_number: 'not valid' };
};

const validateNotEmptyKeys = (obj, keys) =>
  (keys || []).reduce(
    (acc, key) => ({ ...acc, ...validateEmpty(obj, key) }),
    {}
  );

const validateContactPhone = (contactPhone) =>
  /^\+?[0-9]\d{1,14}$/.test(contactPhone) ? {} : { phone: 'error' };

const validateFiscalInfo = ({ fiscalInfo, country }) => ({
  ...validateNotEmptyKeys(fiscalInfo, ['tax_number']),
  ...validateNotEmptyKeys(fiscalInfo.billing_contact, [
    'name',
    'last_name',
    'phone',
    'email',
  ]),
  ...validateContactPhone(
    (fiscalInfo.billing_contact && fiscalInfo.billing_contact.phone) || ''
  ),
  ...validateTaxNumber({ taxNumber: fiscalInfo.tax_number, country }),
});

const validatePaymentMethod = ({ paymentMethod, country }) => {
  const options = constants.PAYMENT_METHOD({ country })[
    (paymentMethod.payment_method || '').toLowerCase()
  ];

  return {
    ...validateNotEmptyKeys(paymentMethod, ['payment_method']),
    ...(options &&
      options.limit &&
      paymentMethod.payment_term &&
      paymentMethod.payment_term.number_of_days &&
      paymentMethod.payment_term.number_of_days >= 0 &&
      paymentMethod.payment_term.number_of_days <= options.limit),
    ...(options && options.validations
      ? options.validations(paymentMethod.payment_term)
      : {}),
    ...validateIBANIfNeeded(paymentMethod),
  };
};

const validatePaymentData = ({ paymentData, country }) => {
  let errors = {};

  const { fiscal_info, payment_method } = paymentData;

  const fiscalInfoErrors = validateFiscalInfo({
    fiscalInfo: fiscal_info,
    country,
  });
  const paymentMethodErrors = validatePaymentMethod({
    paymentMethod: payment_method,
    country,
  });

  errors = { ...fiscalInfoErrors, ...paymentMethodErrors };
  return { isValid: isEmpty(errors), errors };
};

export default validatePaymentData;
